import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

export interface PlanRestrictionContactModalProps {
  open: boolean;
  onClose: () => void;
  /** Optional message from the server (e.g. API `detail`) shown below the default copy. */
  detail?: string | null;
}

const outlinedPillSx = (theme: {
  customColors?: {
    themePrimary?: string;
    themeSecondary?: string;
  };
  palette: { primary: { main: string } };
}) => ({
  border: `1px solid ${theme.customColors?.themePrimary || theme.palette.primary.main}`,
  backgroundColor: theme.customColors?.themeSecondary,
  color: theme.customColors?.themePrimary || theme.palette.primary.main,
  borderRadius: '50px',
  textTransform: 'capitalize' as const,
  '&:hover': {
    border: `1px solid ${theme.customColors?.themePrimary || theme.palette.primary.main}`,
    backgroundColor:
      theme.customColors?.themePrimary || theme.palette.primary.main,
    color: theme.customColors?.themeSecondary || '#fff'
  }
});

const containedPillSx = (theme: {
  customColors?: {
    themePrimary?: string;
    themeSecondary?: string;
  };
  palette: { primary: { main: string } };
}) => ({
  border: `1px solid ${theme.customColors?.themePrimary || theme.palette.primary.main}`,
  backgroundColor:
    theme.customColors?.themePrimary || theme.palette.primary.main,
  color: theme.customColors?.themeSecondary || '#fff',
  borderRadius: '50px',
  textTransform: 'capitalize' as const,
  '&:hover': {
    backgroundColor:
      theme.customColors?.themePrimary || theme.palette.primary.main,
    opacity: 0.9
  }
});

/**
 * Modal shown when a plan- or tier-restricted action is blocked.
 * Wire `open` / `onClose` from parent state or context when you integrate error handling.
 */
const PlanRestrictionContactModal: React.FC<
  PlanRestrictionContactModalProps
> = ({ open, onClose, detail }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '20px',
          boxShadow: '0 12px 48px rgba(0,0,0,0.15)',
          maxHeight: '90vh'
        }
      }}
      aria-labelledby="plan-restriction-contact-title"
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Typography
          id="plan-restriction-contact-title"
          variant="h5"
          sx={{
            fontWeight: 700,
            fontSize: '24px',
            color: (theme) =>
              theme.customColors?.themeNeutralPrimaryColor ??
              theme.palette.text.primary
          }}
        >
          This action isn&apos;t available on your current plan
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          aria-label="Close"
          sx={{
            color: (theme) =>
              theme.customColors?.themeNeutralSecondaryColor ??
              theme.palette.text.secondary,
            '&:hover': {
              backgroundColor: 'action.hover'
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: 3, py: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography
            variant="body2"
            sx={{
              color: (theme) =>
                theme.customColors?.themeNeutralSecondaryColor ??
                theme.palette.text.secondary
            }}
          >
            To use this feature, please get in touch with us. We&apos;ll help
            you find the right option for your team.
          </Typography>
          {detail ? (
            <Typography
              variant="body2"
              sx={{
                whiteSpace: 'pre-wrap',
                color: (theme) =>
                  theme.customColors?.themeNeutralSecondaryColor ??
                  theme.palette.text.secondary,
                px: 1.5,
                py: 1,
                borderRadius: 1,
                bgcolor: 'action.hover'
              }}
            >
              {detail}
            </Typography>
          ) : null}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
        <Link
          href={'mailto:sales@dokai.ai'}
          target="_blank"
          rel="noopener noreferrer"
        >
          <Button variant="contained" sx={(theme) => containedPillSx(theme)}>
            Contact us
          </Button>
        </Link>
        <Button onClick={onClose} sx={(theme) => outlinedPillSx(theme)}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PlanRestrictionContactModal;
